import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { NumberInput } from '@hivebrite/design-system/Inputs';

import { shouldDisplayError } from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import { validateNumber } from './validate';

const Number = ({
  ariaDescribedBy,
  ariaLabel,
  clearable = true,
  disabled = false,
  icon,
  id,
  info,
  infoIcon,
  label,
  labelMultiline = true,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  tooltip,
  validate: {
    required = false,
    minimum = undefined,
    maximum = undefined,
    maximumErrorLabel = '',
    minimumErrorLabel = '',
    maximumNumberOfDecimals = undefined,
    customValidator = undefined,
  } = {},
  wrapperProps = {},
}) => {
  const formik = useFormikContext();
  const htmlId = id || name;

  const handleValidate = useCallback(
    (value) =>
      validateNumber({
        customValidator,
        maximum,
        maximumErrorLabel,
        maximumNumberOfDecimals,
        minimum,
        minimumErrorLabel,
        required,
        value,
      }),
    [
      customValidator,
      maximum,
      maximumErrorLabel,
      maximumNumberOfDecimals,
      minimum,
      minimumErrorLabel,
      required,
    ],
  );
  const handleChange = useCallback(
    (e) => {
      formik.handleChange(e);
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [formik, onChange],
  );
  const handleBlur = useCallback(
    (e) => {
      formik.handleBlur(e);
      if (onBlur) {
        onBlur();
      }
    },
    [formik, onBlur],
  );

  return (
    <Box mb={6} {...wrapperProps}>
      <Field name={name} validate={handleValidate}>
        {({
          field, // { name, value, onChange, onBlur }
          meta, // { error, touched, value }
          form,
        }) => (
          <NumberInput
            {...field}
            ariaDescribedBy={ariaDescribedBy}
            ariaLabel={ariaLabel}
            clearable={clearable}
            dataTestId="formik-number"
            disabled={disabled}
            error={shouldDisplayError({ meta, form })}
            icon={icon}
            id={htmlId}
            info={info}
            infoIcon={infoIcon}
            initialValue={meta.initialValue}
            label={label}
            labelMultiline={labelMultiline}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={onFocus}
            placeholder={placeholder}
            required={required}
            tooltip={tooltip}
            touched={meta.touched}
          />
        )}
      </Field>
    </Box>
  );
};

Number.propTypes = {
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.any,
  id: PropTypes.string,
  info: PropTypes.string,
  infoIcon: PropTypes.any,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelMultiline: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
    minimum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maximum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maximumErrorLabel: PropTypes.string,
    maximumNumberOfDecimals: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    customValidator: PropTypes.func,
  }),
  wrapperProps: PropTypes.object,
};

export default Number;
